import {
  ContextMenu,
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from "@/components/ui";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { type Contact } from "@/lib/types";
import { copyToClipboard } from "@/lib/utils/copyToClipboard";
import {
  formatContactsName,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";
import {
  Clipboard,
  ExternalLink,
  Mail,
  MessageSquare,
  Phone,
  PhoneCall,
  User,
} from "lucide-react";
import { type ReactNode } from "react";

type ContactRowContextMenuProps = {
  contact: Contact;
  trigger: ReactNode;
};

function getPrimaryValue<T extends { isPrimary: boolean | null }>(
  values: T[],
): T | undefined {
  return values.find((value) => value.isPrimary) ?? values[0];
}

export function ContactRowContextMenu({
  contact,
  trigger,
}: ContactRowContextMenuProps) {
  const { openCommunicationModal, openPhoneCallLogModal } = useGlobalModal();
  const primaryEmail = getPrimaryValue(contact.emails)?.address;
  const primaryPhone = getPrimaryValue(contact.phones)?.number;
  const emails = contact.emails
    .map((email) => email.address)
    .filter((email): email is string => Boolean(email));
  const phones = contact.phones
    .map((phone) => phone.number)
    .filter((phone): phone is string => Boolean(phone));
  const name = formatContactsName(contact);
  const canSendText = true;

  return (
    <ContextMenu entityName="contact" trigger={trigger}>
      <ContextMenuItem
        onSelect={() => {
          window.open(
            `/contacts/${contact.id}`,
            "_blank",
            "noopener,noreferrer",
          );
        }}
      >
        <ExternalLink />
        Open in new tab
      </ContextMenuItem>
      <ContextMenuItem
        disabled={!primaryPhone || !canSendText}
        onSelect={() => {
          openCommunicationModal({
            contactId: contact.id,
            setSelectedContactId: () => {},
          });
        }}
      >
        <MessageSquare />
        Send text
      </ContextMenuItem>
      <ContextMenuSub>
        <ContextMenuSubTrigger>
          <Phone />
          Call
        </ContextMenuSubTrigger>
        <ContextMenuSubContent>
          {contact.phones.length === 0 ? (
            <ContextMenuItem disabled>No phones on file</ContextMenuItem>
          ) : (
            contact.phones.map((phone) => (
              <ContextMenuItem
                key={phone.id}
                onSelect={() => {
                  const sanitizedNumber = phone.number.replace(/[^0-9+]/g, "");
                  if (sanitizedNumber) {
                    window.open(`tel:${sanitizedNumber}`, "_self");
                  }
                }}
              >
                <Phone />
                {phone.label ? `${phone.label}: ` : ""}
                {formatLocalOrIntlPhoneNumber(phone)}
              </ContextMenuItem>
            ))
          )}
          <ContextMenuItem
            onSelect={() => {
              openPhoneCallLogModal({ contactId: contact.id });
            }}
          >
            <PhoneCall />
            Call log
          </ContextMenuItem>
        </ContextMenuSubContent>
      </ContextMenuSub>
      <ContextMenuSub>
        <ContextMenuSubTrigger>
          <Clipboard />
          Copy
        </ContextMenuSubTrigger>
        <ContextMenuSubContent>
          <ContextMenuItem
            disabled={!name}
            onSelect={() => {
              void copyToClipboard(name ?? "", "Contact name");
            }}
          >
            <User />
            Copy name
          </ContextMenuItem>
          {emails.length > 1 ? (
            <ContextMenuSub>
              <ContextMenuSubTrigger>
                <Mail />
                Copy email
              </ContextMenuSubTrigger>
              <ContextMenuSubContent>
                {emails.map((email) => (
                  <ContextMenuItem
                    key={email}
                    onSelect={() => {
                      void copyToClipboard(email, "Contact email");
                    }}
                  >
                    {email}
                  </ContextMenuItem>
                ))}
              </ContextMenuSubContent>
            </ContextMenuSub>
          ) : (
            <ContextMenuItem
              disabled={!primaryEmail}
              onSelect={() => {
                void copyToClipboard(primaryEmail ?? "", "Contact email");
              }}
            >
              <Mail />
              Copy email
            </ContextMenuItem>
          )}
          {phones.length > 1 ? (
            <ContextMenuSub>
              <ContextMenuSubTrigger>
                <Phone />
                Copy phone
              </ContextMenuSubTrigger>
              <ContextMenuSubContent>
                {phones.map((phone) => (
                  <ContextMenuItem
                    key={phone}
                    onSelect={() => {
                      void copyToClipboard(phone, "Contact phone number");
                    }}
                  >
                    {phone}
                  </ContextMenuItem>
                ))}
              </ContextMenuSubContent>
            </ContextMenuSub>
          ) : (
            <ContextMenuItem
              disabled={!primaryPhone}
              onSelect={() => {
                void copyToClipboard(
                  primaryPhone ?? "",
                  "Contact phone number",
                );
              }}
            >
              <Phone />
              Copy phone
            </ContextMenuItem>
          )}
        </ContextMenuSubContent>
      </ContextMenuSub>
    </ContextMenu>
  );
}
